// A plan board (shared/boardKinds.ts): the plan for a big build, made with the board's own agent before
// anything is built, then handed to the user's coding agent, which keeps it up to date over MCP as it builds.
// Its content is a doc board's pages: the first page at the top is the plan's Overview, and every other page
// is a step of the plan (a "part" in the code), with its status, its tasks as the page's to-dos, and a short
// history of what happened to it, the agent's plain messages to the user among it. Questions for the user are entries of their own, each inside the part it is about (or at
// the top, for the whole plan), so they can be answered with a click and read back by any agent.
//
// No import from shared/entries.ts here: it imports this file for its cleaner.

import { asBody, plainText, type DocBody, type DocNode } from "./docblocks";
import { isDismissed, type Dismissed } from "./dismiss";
import { PICTURE_KEY } from "./imageInfo";
import type { Entry, ID } from "./types";

/**
 * Where a part of the plan stands. Waiting is on someone outside the build (Google's review, a customer), so it never
 * waits on the user; blocked needs the user; later is set aside on purpose (Plan Board Fixes 3 and 10, 22 Oct).
 */
export const PLAN_STATUSES = ["planned", "building", "built", "blocked", "waiting", "later", "dropped"] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

/** A status in an agent's words and the history's: "Built: tests pass" (a history line is read back by these). */
export const STATUS_NAME: Record<PlanStatus, string> = {
  planned: "Planned",
  building: "Building",
  built: "Built",
  blocked: "Blocked",
  waiting: "Waiting",
  later: "Later",
  dropped: "Dropped",
};

/** A status as a person reads it on the board: built is Done (David, 22 Oct: "I can't mark them as done"). */
export const STATUS_LABEL: Record<PlanStatus, string> = { ...STATUS_NAME, built: "Done" };

/** What each status means, for a person choosing one and an agent reading the plan. */
export const STATUS_BLURB: Record<PlanStatus, string> = {
  planned: "Not started yet.",
  building: "Being worked on now.",
  built: "Done, with what proves it.",
  blocked: "Needs you: a decision, or something only you can do.",
  waiting: "Waiting on someone outside the build, such as Google's review. Not on you.",
  later: "Set aside on purpose, for later.",
  dropped: "No longer part of the plan, with why.",
};

/** A status that keeps a step out of the plan's progress: it is not to be done now. */
export const isSetAside = (s: PlanStatus) => s === "dropped" || s === "later";

export function isPlanStatus(x: unknown): x is PlanStatus {
  return typeof x === "string" && (PLAN_STATUSES as readonly string[]).includes(x);
}

/**
 * One thing that happened to a step: its status moved, tasks were ticked ("step", a name kept from before tasks
 * were called tasks), the plan changed, a note for its history, or "say": the agent's plain message to the user,
 * which the plan's thread reads as a chat.
 */
export interface PlanLog {
  at: number;
  by: string | null;
  kind: "status" | "step" | "change" | "note" | "say";
  text: string;
  /** a change to the plan, in plain words (David, 19 Oct: "really clear as to what has actually changed"): what the plan said, what is done instead, and why */
  was?: string;
  now?: string;
  why?: string;
  /** on a line that blocks a step: what it waits on, in a few plain words the user reads at a glance (Plan Board Fixes, 22 Oct) */
  on?: string;
  /** on the user's note: the agents it reached, and when; an empty list until one has it, and left out on a note from before the board kept this */
  got?: { by: string; at: number }[];
}

/** How many of a part's history lines it keeps, newest last. */
export const MAX_LOG = 30;

/** A change's plain words are short: a few words for was and now, a sentence for why. */
export const CHANGE_WORDS = { was: 160, now: 160, why: 280 } as const;

/** What a blocked step waits on is a few words, read at a glance in Needs you: "Google's store review". */
export const WAITING_ON_MAX = 80;

/** The agents a note reached, as kept: each a name and a time, the first few. */
function cleanGot(raw: unknown[]): { by: string; at: number }[] {
  const out: { by: string; at: number }[] = [];
  for (const g of raw) {
    const x = g && typeof g === "object" ? (g as Record<string, unknown>) : null;
    if (!x || typeof x.by !== "string" || !x.by.trim() || typeof x.at !== "number" || !Number.isFinite(x.at)) continue;
    if (!out.some((o) => o.by === x.by)) out.push({ by: x.by.slice(0, 80), at: x.at });
  }
  return out.slice(0, 4);
}

/** A part's history as kept: each line cleaned, the newest MAX_LOG. */
export function cleanLog(raw: unknown): PlanLog[] {
  if (!Array.isArray(raw)) return [];
  const out: PlanLog[] = [];
  const short = (v: unknown, max: number) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "");
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const x = r as Record<string, unknown>;
    const text = typeof x.text === "string" ? x.text.replace(/\s+/g, " ").trim().slice(0, 1000) : "";
    if (!text) continue;
    const kind = x.kind === "status" || x.kind === "step" || x.kind === "change" || x.kind === "say" ? x.kind : "note";
    const line: PlanLog = { at: typeof x.at === "number" && Number.isFinite(x.at) ? x.at : 0, by: typeof x.by === "string" ? x.by.slice(0, 80) : null, kind, text };
    if (kind === "change")
      for (const k of ["was", "now", "why"] as const) {
        const v = short(x[k], CHANGE_WORDS[k]);
        if (v) line[k] = v;
      }
    if (kind === "status") {
      const on = short(x.on, WAITING_ON_MAX);
      if (on) line.on = on;
    }
    if (kind === "note" && line.by === null && Array.isArray(x.got)) line.got = cleanGot(x.got);
    out.push(line);
  }
  return out.slice(-MAX_LOG);
}

/** A change line's plain words, when its agent gave what is done instead or why: a lone "was" says nothing on its own. */
export const changeOf = (l: Pick<PlanLog, "was" | "now" | "why">): { was: string | null; now: string | null; why: string | null } | null =>
  l.now || l.why ? { was: l.was ?? null, now: l.now ?? null, why: l.why ?? null } : null;

/** A history line as an agent reads it in read_plan: a change in its plain words, was and now and why, else its text. */
export function lineWords(l: PlanLog): string {
  const c = l.kind === "change" ? changeOf(l) : null;
  if (!c) return l.text;
  return ["Changed the plan.", c.was ? `Was: ${c.was}.` : "", c.now ? `Now: ${c.now}.` : "", c.why ? `Why: ${c.why}` : ""].filter(Boolean).join(" ").replace(/\.\./g, ".");
}

/** Words that end in a full stop without ending a sentence. */
const SHORT = /(?:^|\s)(?:e\.g|i\.e|etc|vs|approx|incl|no|fig|Mr|Mrs|Ms|Dr|St)\.$/i;

/** A long text's first sentence, for a line to read at a glance: the rest opens on a click. */
export function firstSentence(text: string, max = 140): string {
  const t = text.replace(/\s+/g, " ").trim();
  let end = -1;
  for (const m of t.matchAll(/[.!?](?=\s|$)/g)) {
    const upTo = t.slice(0, m.index! + 1);
    if (m[0] === "." && SHORT.test(upTo)) continue;
    end = m.index! + 1;
    break;
  }
  const s = end > 0 && end <= max ? t.slice(0, end) : t;
  return s.length <= max ? s : `${s.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
}

/** A part's status as it reads: one never set is planned, or building once a task is ticked (Fixes 10, 30 Oct); one set, by hand or by an agent, reads as set (Fixes 13). */
export function statusOf(page: Pick<Entry, "data">): PlanStatus {
  if (isPlanStatus(page.data.status)) return page.data.status;
  return stepCount(page.data.body).done > 0 ? "building" : "planned";
}

export function logOf(page: Pick<Entry, "data">): PlanLog[] {
  return cleanLog(page.data.log);
}

// ---------------------------------------------------------------------------
// Previews (Features 3, 22 Oct): the screenshots and mockups the coding agent sends as it builds, kept on the step
// they are about (the Overview's for the whole plan), shown in the plan's Previews, on the step's page and in the
// thread under the message they came with. The owner's alone: a shared link never has them (publicEntry).

/** A picture the agent sent to a step: one the board keeps, by its key, with its caption and when it came. */
export interface PlanPreview {
  key: string;
  w: number;
  h: number;
  caption: string;
  /** its update_plan call's time, which the message it came with has too, so the thread shows it there */
  at: number;
  by: string | null;
  /** one of a pair, so a visual change is judged side by side */
  pair?: "before" | "after";
}

/** How many pictures a step keeps, newest last: the oldest go first. */
export const MAX_PREVIEWS = 40;
/** How many pictures one update_plan sends. */
export const MAX_PREVIEWS_SENT = 6;
export const PREVIEW_CAPTION_MAX = 200;

/** A step's pictures as kept: only keys the board's own picture store makes, each cleaned, the newest MAX_PREVIEWS. */
export function cleanPreviews(raw: unknown): PlanPreview[] {
  if (!Array.isArray(raw)) return [];
  const out: PlanPreview[] = [];
  const side = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 20_000 ? Math.round(v) : 0);
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const x = r as Record<string, unknown>;
    if (typeof x.key !== "string" || !PICTURE_KEY.test(x.key)) continue;
    const caption = typeof x.caption === "string" ? x.caption.replace(/\s+/g, " ").trim().slice(0, PREVIEW_CAPTION_MAX) : "";
    const at = typeof x.at === "number" && Number.isFinite(x.at) ? x.at : 0;
    out.push({ key: x.key, w: side(x.w), h: side(x.h), caption, at, by: typeof x.by === "string" ? x.by.slice(0, 80) : null, ...(x.pair === "before" || x.pair === "after" ? { pair: x.pair } : {}) });
  }
  return out.slice(-MAX_PREVIEWS);
}

export const previewsOf = (page: Pick<Entry, "data">): PlanPreview[] => cleanPreviews(page.data.previews);

/**
 * One thing to look at in Previews: a picture on its own, or a before and its after, side by side. An after pairs
 * with the step's latest before that has none yet, sent in the same update or an earlier one (a screenshot before
 * the step starts, one after it is built).
 */
export interface PreviewShot {
  id: string;
  page: Entry;
  /** the newest of its pictures */
  at: number;
  by: string | null;
  pics: PlanPreview[];
  paired: boolean;
}

/** A step's shots, oldest first. */
export function shotsOf(page: Entry): PreviewShot[] {
  const shots: PreviewShot[] = [];
  const open: PreviewShot[] = [];
  // in the order they were sent, a call's befores ahead of its afters: files from one call can arrive in any order
  const sent = previewsOf(page)
    .map((p, i) => ({ p, i }))
    .sort((a, b) => a.p.at - b.p.at || (a.p.pair === "before" ? 0 : 1) - (b.p.pair === "before" ? 0 : 1) || a.i - b.i);
  sent.forEach(({ p, i }) => {
    if (p.pair === "after") {
      // a before from the same call first, else the step's last before still waiting for its after
      const k = open.findIndex((s) => s.pics[0].at === p.at);
      const before = k >= 0 ? open.splice(k, 1)[0] : open.pop();
      if (before) {
        before.pics.push(p);
        before.paired = true;
        before.at = Math.max(before.at, p.at);
        return;
      }
    }
    const shot: PreviewShot = { id: `${page.id}:${i}:${p.key}`, page, at: p.at, by: p.by, pics: [p], paired: false };
    shots.push(shot);
    if (p.pair === "before") open.push(shot);
  });
  return shots;
}

/** The plan's Previews: by step, the step with the newest first, each step's newest first; and how many pictures in all. */
export function planPreviews(pages: Entry[]): { groups: { page: Entry; overview: boolean; shots: PreviewShot[] }[]; count: number } {
  const overview = overviewOf(pages);
  const groups = [...(overview ? [overview] : []), ...partsOf(pages).map((r) => r.page)]
    .map((page) => ({ page, overview: page === overview, shots: shotsOf(page).sort((a, b) => b.at - a.at) }))
    .filter((g) => g.shots.length)
    .sort((a, b) => b.shots[0].at - a.shots[0].at);
  return { groups, count: groups.reduce((n, g) => n + g.shots.reduce((m, s) => m + s.pics.length, 0), 0) };
}

/** Words an agent wrote with an id in them, "(question e_...)", without it: the user reads the words. */
export const withoutIds = (text: string) =>
  text
    .replace(/\s*\((?:[a-z]+ )?(?:id )?e_[A-Za-z0-9_-]{6,}\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

/** What a blocked step waits on, as the user reads it in Needs you, and since when. */
export interface Blocker {
  /** a few plain words: the agent's own (waiting_on), else the first sentence of what it said when it blocked the step; empty when it said nothing */
  on: string;
  /** on is the agent's own few words, read as "waiting on ..." */
  plain: boolean;
  /** when the step was blocked: what has happened since is not new to the user */
  since: number;
  by: string | null;
  /** the rest of what was said, to open: the technical note */
  detail: string | null;
}

/** A blocked step's blocker, from the line that blocked it last; null when the step is not blocked (Plan Board Fixes, 22 Oct). */
export const blockerOf = (page: Pick<Entry, "data" | "createdAt">): Blocker | null => heldOf(page, "blocked");

/** What a waiting step waits on, outside the build, and since when; null when the step is not waiting (Plan Board Fixes 3, 22 Oct). */
export const waitOf = (page: Pick<Entry, "data" | "createdAt">): Blocker | null => heldOf(page, "waiting");

/** A step held up, blocked or waiting: what it waits on, from the line that set it so last. */
function heldOf(page: Pick<Entry, "data" | "createdAt">, held: "blocked" | "waiting"): Blocker | null {
  if (statusOf(page) !== held) return null;
  const log = logOf(page);
  for (let i = log.length - 1; i >= 0; i--) {
    const l = log[i];
    const s = l.kind === "status" ? statusLine(l.text) : null;
    if (s?.status !== held) continue;
    const note = s.note ? withoutIds(s.note) : "";
    if (l.on) return { on: l.on, plain: true, since: l.at, by: l.by, detail: note || null };
    // a step blocked before the few words: the first sentence of the note it was blocked with, else of its message
    const said = note || withoutIds(log.find((x) => x.kind === "say" && x.at === l.at && x.by === l.by)?.text ?? "");
    const on = said ? firstSentence(said, 100) : "";
    return { on, plain: false, since: l.at, by: l.by, detail: note && note !== on ? note : null };
  }
  // blocked by a line the history has let go (it keeps the newest MAX_LOG)
  return { on: "", plain: false, since: page.createdAt ?? 0, by: null, detail: null };
}

/**
 * When a plan's step was blocked: what has happened on it since is not new to the user. A blocked step they dismissed
 * (or said is still waiting) comes back only when it is blocked again, never with every message on it (Plan Board
 * Fixes, 22 Oct: an agent's "it's live" on a blocked step brought the alarm back).
 */
export const blockedSince = (p: Pick<Entry, "data" | "createdAt">) => blockerOf(p)?.since ?? p.createdAt ?? 0;

/** A plan's blocked steps as the user is shown them, in Needs you, the bell and the counts: not one dismissed since it was blocked. */
export function shownBlocked(d: Dismissed | null | undefined, entries: Entry[]): Entry[] {
  return needsYou(entries).blocked.filter((p) => !isDismissed(d, `b:${p.id}`, blockedSince(p)));
}

/** A blocker as a person reads it after a step's name: "is blocked, waiting on Google's review", "is blocked: Needs the key.", "is blocked." */
export const blockedWords = (b: Blocker | null) => (b?.on ? (b.plain ? `is blocked, waiting on ${b.on}` : `is blocked: ${b.on}`) : "is blocked");

/**
 * A step's latest, in a few words for the step list (Plan Board Fixes 6, 22 Oct): what it waits on when it is held
 * up, else its newest line: the agent's message, the user's note, or what happened. Empty for a step with no history.
 */
export function latestWords(page: Pick<Entry, "data" | "createdAt">): string {
  const s = statusOf(page);
  const held = s === "blocked" ? blockerOf(page) : s === "waiting" ? waitOf(page) : null;
  if (held?.on) return held.plain ? `Waiting on ${held.on}` : held.on;
  const l = logOf(page).at(-1);
  if (!l) return "";
  const st = l.kind === "status" ? statusLine(l.text) : null;
  if (l.kind === "say") return firstSentence(l.text, 90);
  if (l.kind === "note" && l.by === null) return `Your note: ${firstSentence(l.text, 80)}`;
  if (st) return st.status === "built" ? "Done" : st.status === "later" ? "Set aside for later" : st.status === "dropped" ? "Dropped" : st.status === "building" ? "Being worked on" : st.status === "planned" ? "" : STATUS_LABEL[st.status];
  if (l.kind === "step") return l.text.startsWith("Done: ") ? "Tasks ticked" : "Tasks unticked";
  if (l.kind === "change") return "Changed the plan";
  return firstSentence(withoutIds(l.text), 90);
}

/** A wait as a person reads it after a step's name: "is waiting on Google's store review", "is waiting: Submitted it.", "is waiting on others". */
export const waitingWords = (b: Blocker | null) => (b?.on ? (b.plain ? `is waiting on ${b.on}` : `is waiting: ${b.on}`) : "is waiting on others");

const byOrder = <E extends Pick<Entry, "order" | "createdAt" | "id">>(a: E, b: E) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1);

/** The plan's pages in reading order, each with how deep it sits: the Overview first, then each part followed by the parts inside it. */
export function planRows(pages: Entry[]): { page: Entry; depth: number }[] {
  const ids = new Set(pages.map((p) => p.id));
  const parentOf = (p: Entry) => (p.parent && ids.has(p.parent) ? p.parent : null);
  const rows: { page: Entry; depth: number }[] = [];
  const walk = (parent: ID | null, depth: number) => {
    for (const p of pages.filter((x) => parentOf(x) === parent).sort(byOrder)) {
      rows.push({ page: p, depth });
      if (depth < 12) walk(p.id, depth + 1);
    }
  };
  walk(null, 0);
  return rows;
}

/** The plan's Overview: its first page at the top. */
export function overviewOf(pages: Entry[]): Entry | null {
  return planRows(pages)[0]?.page ?? null;
}

/** Every part of the plan, in reading order: every page but the Overview (and the pages inside it, which are its notes). */
export function partsOf(pages: Entry[]): { page: Entry; depth: number }[] {
  const rows = planRows(pages);
  const top = rows[0]?.page;
  if (!top) return [];
  // the pages inside the Overview are the overview's own notes, not parts
  const out: { page: Entry; depth: number }[] = [];
  let inOverview = false;
  for (const r of rows) {
    if (r.page.id === top.id) {
      inOverview = true;
      continue;
    }
    if (r.depth === 0) inOverview = false;
    if (!inOverview) out.push(r);
  }
  return out;
}

/** Whether a page is a part of the plan (not its Overview, nor a note inside it). */
export function isPart(pages: Entry[], id: ID): boolean {
  return partsOf(pages).some((r) => r.page.id === id);
}

/** A step: one to-do in a part's page. */
export interface Step {
  text: string;
  done: boolean;
}

/** A to-do's words: its first paragraph's, without the to-dos inside it. */
function itemText(item: DocNode): string {
  const first = (item.content ?? []).find((c) => c.type === "paragraph");
  return first ? plainText(first) : "";
}

/** Every to-do in a page, at any depth, in order. */
export function stepsOf(body: unknown): Step[] {
  const out: Step[] = [];
  const walk = (n: DocNode) => {
    if (n.type === "taskItem") out.push({ text: itemText(n), done: n.attrs?.checked === true });
    n.content?.forEach(walk);
  };
  asBody(body).content.forEach(walk);
  return out;
}

/** How far a set of steps has got: done of total. */
export function stepCount(body: unknown): { done: number; total: number } {
  const s = stepsOf(body);
  return { done: s.filter((x) => x.done).length, total: s.length };
}

/**
 * A step's tasks as they stand: a step done with tasks left open settles them, skipped, so it reads finished and the
 * plan's count catches up (Plan Board Fixes 11, 22 Oct). skipped: those left open on a done step.
 */
export function settledCount(page: Pick<Entry, "data">): { done: number; total: number; skipped: number } {
  const c = stepCount(page.data.body);
  return statusOf(page) === "built" ? { done: c.total, total: c.total, skipped: c.total - c.done } : { ...c, skipped: 0 };
}

/**
 * Tasks done and skipped, said apart for agents (Fixes 9, 28 Oct: a plan with a task skipped read "2 of 2 done", so it
 * looked finished): "1 done, 1 skipped of 2", or "1 of 2 done" with none skipped.
 */
export function taskTally(c: { done: number; total: number; skipped?: number }, noun = ""): string {
  const n = noun ? ` ${noun}` : "";
  return c.skipped ? `${c.done} done, ${c.skipped} skipped of ${c.total}${n}` : `${c.done} of ${c.total}${n} done`;
}

/**
 * Ticks (or unticks) the to-dos a list of words names: the one whose words match exactly (any case), else the
 * one that holds them, when only one does. Says which it found and which it could not.
 */
export function tickSteps(body: unknown, words: string[], done: boolean): { body: DocBody; ticked: string[]; missing: string[] } {
  const doc = asBody(body);
  const items: DocNode[] = [];
  const walk = (n: DocNode) => {
    if (n.type === "taskItem") items.push(n);
    n.content?.forEach(walk);
  };
  doc.content.forEach(walk);
  const pick = new Set<DocNode>();
  const ticked: string[] = [];
  const missing: string[] = [];
  for (const w of words) {
    const want = w.replace(/\s+/g, " ").trim().toLowerCase();
    if (!want) continue;
    let hits = items.filter((i) => itemText(i).toLowerCase() === want);
    if (!hits.length) hits = items.filter((i) => itemText(i).toLowerCase().includes(want));
    if (hits.length !== 1) {
      missing.push(hits.length ? `"${w}" (${hits.length} tasks hold those words; use more of them)` : `"${w}"`);
      continue;
    }
    pick.add(hits[0]);
    ticked.push(itemText(hits[0]));
  }
  const mark = (n: DocNode): DocNode => {
    const next = pick.has(n) ? { ...n, attrs: { ...(n.attrs ?? {}), checked: done } } : n;
    return next.content ? { ...next, content: next.content.map(mark) } : next;
  };
  return { body: { type: "doc", content: doc.content.map(mark) }, ticked, missing };
}

// ---------------------------------------------------------------------------
// Questions for the user

/** A question for the user: a choice of up to four answers, one of them recommended, or their own words. */
export interface QuestionData {
  text: string;
  options: { label: string; detail: string }[];
  /** the option recommended, by its place in options, or null */
  recommended: number | null;
  /** who asked it */
  asked: string | null;
  /** the user's answer: an option's label or their own words; null until answered */
  answer: string | null;
  answeredAt: number | null;
  /** when the agent that asked was handed the answer (wait_for_answer, read_plan or a reply of its own); null until then. The board's alone to set */
  delivered: number | null;
  /** when the agent that asked last waited for the answer (wait_for_answer), so the board shows it waiting. The board's alone to set */
  waited: number | null;
  /** a decision, or a check (try this, sign in, paste this): only decisions are listed as Decided (Plan Board Fixes 8, 22 Oct). Its asker's to say, when it asks */
  kind: "decision" | "check";
  /**
   * Where the answer was given (Features 3, 22 Oct): null on the board, else the app of the coding session the user
   * typed it in ("Claude Code", "T3 Code", "Codex"), which recorded it with answer_plan. The board's alone to set
   */
  via: string | null;
  /** a later answer from the other place, kept as a note on the question: the first answer stands. The board's alone to set */
  also: { text: string; at: number; via: string | null } | null;
}

export const MAX_OPTIONS = 4;

/** Data the board itself made for a question, carrying the fields only it sets (delivered, waited): a client's JSON is never one of these. */
export const BOARD_WRITES = new WeakSet<object>();

const line = (x: unknown, max: number) => (typeof x === "string" ? x.replace(/\s+/g, " ").trim().slice(0, max) : "");

/** A question's fields, cleaned; throws its words through refuse when it has no question in it. */
export function cleanQuestion(raw: Record<string, unknown>, before: QuestionData | null, now: number, refuse: (status: number, message: string) => never): QuestionData {
  const text = "text" in raw ? line(raw.text, 600) : (before?.text ?? "");
  if (!text) refuse(400, "A question needs its words: text.");
  let options = before?.options ?? [];
  if ("options" in raw) {
    if (!Array.isArray(raw.options)) refuse(400, "options is a list of up to four answers to choose from.");
    options = (raw.options as unknown[])
      .map((o) => (typeof o === "string" ? { label: line(o, 140), detail: "" } : o && typeof o === "object" ? { label: line((o as { label?: unknown }).label, 140), detail: line((o as { detail?: unknown }).detail, 400) } : { label: "", detail: "" }))
      .filter((o) => o.label);
    if (options.length > MAX_OPTIONS) refuse(400, `A question has up to ${MAX_OPTIONS} answers to choose from.`);
  }
  const rec = "recommended" in raw ? raw.recommended : (before?.recommended ?? null);
  const recommended = typeof rec === "number" && Number.isInteger(rec) && rec >= 0 && rec < options.length ? rec : null;
  const asked = before ? before.asked : line(raw.asked, 80) || null;
  // whether the asker has the answer, and when it last waited for one: only the board says, never a client
  const board = BOARD_WRITES.has(raw);
  const stamp = (k: "delivered" | "waited") => (board && k in raw ? (typeof raw[k] === "number" && Number.isFinite(raw[k]) ? (raw[k] as number) : null) : (before?.[k] ?? null));
  let delivered = stamp("delivered");
  const waited = stamp("waited");
  let answer = before?.answer ?? null;
  let answeredAt = before?.answeredAt ?? null;
  let via = before?.via ?? null;
  let also = before?.also ?? null;
  if ("answer" in raw) {
    const a = raw.answer === null ? "" : typeof raw.answer === "string" ? raw.answer.trim().slice(0, 2000) : "";
    // where it was answered: a coding session's app, which only the board says (answer_plan); a client's is the board
    const from = board ? line(raw.via, 40) || null : null;
    if (a && answer && a !== answer) {
      // answered already, here or in the session: the first answer stands, and this one is kept as a note on it
      // (Features 3). A change of mind on the board clears the answer first (Change), and then answers again
      if (a !== also?.text) also = { text: a, at: now, via: from };
    } else if (a !== (answer ?? "")) {
      answer = a || null;
      answeredAt = a ? now : null;
      via = a ? from : null;
      if (!a) also = null;
      // a new answer has not reached the asker yet, unless the board says it has (the asker gave it, in its session)
      delivered = board && "delivered" in raw ? delivered : null;
    }
  }
  const kind = before ? (before.kind === "check" ? "check" : "decision") : raw.kind === "check" ? "check" : "decision";
  return { text, options, recommended, asked, answer, answeredAt, delivered, waited, kind, via, also };
}

/** A note kept on a question, as questionOf reads one: anything else is none. */
function alsoOf(v: unknown): QuestionData["also"] {
  if (!v || typeof v !== "object") return null;
  const o = v as { text?: unknown; at?: unknown; via?: unknown };
  return typeof o.text === "string" && o.text && typeof o.at === "number" ? { text: o.text, at: o.at, via: typeof o.via === "string" && o.via ? o.via : null } : null;
}

/**
 * The user's answer to a question as they gave it in their coding session: an option's number ("2", "2."), or its
 * label in any case, reads as that option's label; anything else is their own words. "" when there is nothing.
 */
export function answerFor(q: Pick<QuestionData, "options">, given: unknown): string {
  const words = typeof given === "number" && Number.isFinite(given) ? String(given) : typeof given === "string" ? given.replace(/\s+/g, " ").trim() : "";
  if (!words) return "";
  const n = /^(?:option\s*)?#?(\d)[.)]?$/i.exec(words);
  if (n && Number(n[1]) >= 1 && Number(n[1]) <= q.options.length) return q.options[Number(n[1]) - 1].label;
  const same = q.options.find((o) => o.label.trim().toLowerCase() === words.toLowerCase());
  return same ? same.label : words.slice(0, 2000);
}

export function questionOf(e: Pick<Entry, "data">): QuestionData {
  const d = e.data as Partial<QuestionData>;
  return {
    text: typeof d.text === "string" ? d.text : "",
    options: Array.isArray(d.options) ? d.options.filter((o) => o && typeof o.label === "string") : [],
    recommended: typeof d.recommended === "number" ? d.recommended : null,
    asked: typeof d.asked === "string" ? d.asked : null,
    answer: typeof d.answer === "string" && d.answer ? d.answer : null,
    answeredAt: typeof d.answeredAt === "number" ? d.answeredAt : null,
    delivered: typeof d.delivered === "number" ? d.delivered : null,
    waited: typeof d.waited === "number" ? d.waited : null,
    kind: d.kind === "check" ? "check" : "decision",
    via: typeof d.via === "string" && d.via ? d.via : null,
    also: alsoOf(d.also),
  };
}

/** Whether the board keeps track of the answer reaching its asker: every question answered since it began to (19 Oct). */
export const tracksDelivery = (e: Pick<Entry, "data">): boolean => Object.hasOwn(e.data, "delivered");

/** The asker is waiting for this answer now: it called wait_for_answer in the last 75 seconds (a wait lasts 50). */
export const WAIT_SHOWN_MS = 75_000;
export const isWaitedOn = (e: Pick<Entry, "data">, now: number): boolean => {
  const d = questionOf(e);
  return !d.answer && d.waited !== null && now - d.waited < WAIT_SHOWN_MS;
};

/** The questions on a plan, oldest first; open ones only when asked for. */
export function questionsOf(entries: Entry[], open = false): Entry[] {
  // questions asked in one call share a moment: they keep the order they were asked in (review, 22 Oct: a random id
  // broke the tie, so two asked together came back either way round)
  return entries.filter((e) => e.t === "question" && (!open || !questionOf(e).answer)).sort((a, b) => a.createdAt - b.createdAt || a.order - b.order || (a.id < b.id ? -1 : 1));
}

/** What waits on the user: questions not answered yet, and parts blocked with no question open on them. */
export function needsYou(entries: Entry[]): { questions: Entry[]; blocked: Entry[]; count: number } {
  const questions = questionsOf(entries, true);
  const asked = new Set(questions.map((q) => q.parent ?? null));
  const blocked = partsOf(entries.filter((e) => e.t === "page"))
    .map((r) => r.page)
    .filter((p) => statusOf(p) === "blocked" && !asked.has(p.id));
  return { questions, blocked, count: questions.length + blocked.length };
}

// ---------------------------------------------------------------------------
// The plan's stream: everything that happened on it, in order, as the user and their agent see it

/** One thing that happened on the plan: a line of a part's history, a question asked, or one answered. */
export interface PlanEvent {
  /** stable, for React */
  key: string;
  at: number;
  /** who: an agent's name, or null for the user */
  by: string | null;
  /** "pictures": the agent's pictures sent with no other word, which only the thread makes (Previews) */
  kind: PlanLog["kind"] | "asked" | "answered" | "pictures";
  text: string;
  /** the part it is about, or null for the whole plan */
  part: Entry | null;
  /** a status line's status */
  status?: PlanStatus;
  question?: Entry;
  /** a change line's plain words, when its agent gave them */
  shift?: { was: string | null; now: string | null; why: string | null };
  /** a line that blocked its step: what it waits on, in the agent's few words */
  on?: string;
  /** the user's note: the agents it reached (left out on a note from before the board kept this) */
  got?: { by: string; at: number }[];
}

/** A status line's status and what was said with it ("Built: tests pass"), or null when it is not one. */
export function statusLine(text: string): { status: PlanStatus; note: string } | null {
  for (const s of PLAN_STATUSES) {
    const name = STATUS_NAME[s];
    if (text === name) return { status: s, note: "" };
    if (text.startsWith(`${name}: `)) return { status: s, note: text.slice(name.length + 2) };
  }
  return null;
}

/**
 * One thing that happened to a step, as its History tells it (David, 19 Oct: plain and short, the technical detail
 * under it to open): everything one update_plan call wrote, as one item.
 */
export interface HistoryItem {
  at: number;
  by: string | null;
  /** the plain line: the agent's own message when it gave one, the user's own words, or what happened in a few words */
  line: string;
  /** the rest, to open: what proves it, the note, what changed and why, the tasks ticked */
  detail: string[];
  status: PlanStatus | null;
  change: boolean;
  /** a change's plain words, shown side by side under its line, never folded away */
  shift: { was: string | null; now: string | null; why: string | null } | null;
  /** the agent said it in its own words (a message) */
  said: boolean;
}

const DID: Record<PlanStatus, string> = { planned: "Moved this step back to planned.", building: "Started building this step.", built: "Finished this step.", blocked: "Got stuck on this step.", waiting: "Set this step to wait on others.", later: "Set this step aside for later.", dropped: "Dropped this step." };
const MARKED: Record<PlanStatus, string> = { planned: "Marked it planned.", building: "Marked it building.", built: "Marked it done.", blocked: "Marked it blocked.", waiting: "Marked it waiting on others.", later: "Set it aside for later.", dropped: "Marked it dropped." };

export function historyOf(log: PlanLog[]): HistoryItem[] {
  type Work = HistoryItem & { message: string | null; note: string | null; ticked: number; first: boolean; gist: string | null; changeText?: string; on: string | null };
  const items: Work[] = [];
  let anyStatus = false;
  // a full log may have let older lines go, the step's making among them
  const whole = log.length < MAX_LOG;
  for (const l of log) {
    let it = items[items.length - 1];
    if (!it || it.at !== l.at || it.by !== l.by) {
      it = { at: l.at, by: l.by, line: "", detail: [], status: null, change: false, shift: null, said: false, message: null, note: null, ticked: 0, first: false, gist: null, on: null };
      items.push(it);
    }
    if (l.kind === "say") it.message = l.text;
    else if (l.kind === "status") {
      const s = statusLine(l.text);
      if (s) {
        it.status = s.status;
        // a step's first status is its making: planned then is being added, never being moved back
        it.first = !anyStatus;
        anyStatus = true;
        // what a blocked step waits on, in the agent's few words: the line itself, or first under its message
        if (l.on) it.on = l.on;
        if (s.note) it.detail.push(s.note);
      } else it.detail.push(l.text);
    } else if (l.kind === "step") {
      const done = /^Done: (.*)$/.exec(l.text);
      const undone = /^Not done after all: (.*)$/.exec(l.text);
      if (done) it.ticked += done[1].split("; ").length;
      it.detail.push(done ? `Tasks done: ${done[1]}` : undone ? `Tasks not done after all: ${undone[1]}` : l.text);
    } else if (l.kind === "change") {
      it.change = true;
      it.shift = changeOf(l);
      if (it.shift) {
        // the plain words show under the line; a technical note given with them opens under Details
        if (l.text !== it.shift.why && l.text !== it.shift.now) it.detail.push(l.text);
      } else {
        // a change from before plain words: its first sentence is the line, the whole of it opens under it
        it.gist = firstSentence(l.text);
        it.changeText = l.text;
      }
    } else if (l.by === null) it.note = l.text;
    else it.detail.push(l.text);
  }
  return items.map(({ message, note, ticked, first, gist, changeText, on, ...it }) => {
    const tasks = `Ticked off ${ticked} task${ticked === 1 ? "" : "s"}.`;
    // the oldest planned line is the step's making only when nothing older was let go (MAX_LOG): then it says what it is
    const made = first && it.status === "planned";
    const line = message
      ? message
      : it.by === null
        ? (note ?? (made ? (whole ? "Added it to the plan." : "Marked it planned.") : it.status ? MARKED[it.status] : "Changed this step."))
        : made
          ? whole
            ? "Added this step to the plan."
            : "Planned this step."
          : it.status
            ? it.status === "blocked" && on
              ? `Got stuck on this step, waiting on ${on}.`
              : it.status === "waiting" && on
                ? `Waiting on ${on}.`
                : DID[it.status]
            : it.change
              ? gist
                ? `Changed the plan: ${gist}`
                : "Changed the plan for this step."
              : ticked
                ? tasks
                : "Left a note.";
    // an old change's words: the rest of them under its line when the line is their first sentence (all of them when
    // that sentence was cut short), or all of them when the line is another
    const rest = gist && !gist.endsWith("…") && changeText?.startsWith(gist) ? changeText.slice(gist.length).trim() : changeText;
    const told = changeText === undefined ? it.detail : line === `Changed the plan: ${gist}` ? (rest ? [rest, ...it.detail] : it.detail) : [`Changed: ${changeText}`, ...it.detail];
    const detail = on && message ? [`Waiting on ${on}.`, ...told] : told;
    return { ...it, line, detail, said: !!message };
  });
}

/** Everything that happened on a plan, oldest first: each part's history (and the Overview's), and its questions. */
export function planEvents(entries: Entry[]): PlanEvent[] {
  const pages = entries.filter((e) => e.t === "page");
  const overview = overviewOf(pages);
  const parts = new Map(partsOf(pages).map((r) => [r.page.id, r.page]));
  const out: PlanEvent[] = [];
  for (const page of [...(overview ? [overview] : []), ...parts.values()]) {
    const part = page === overview ? null : page;
    logOf(page).forEach((l) => {
      const s = l.kind === "status" ? statusLine(l.text) : null;
      const shift = l.kind === "change" ? changeOf(l) : null;
      out.push({ key: `${page.id}:${l.at}:${l.kind}:${l.by ?? ""}:${l.text.slice(0, 80)}`, at: l.at, by: l.by, kind: l.kind, text: s ? s.note : l.text, part, ...(s ? { status: s.status } : {}), ...(shift ? { shift } : {}), ...(l.on ? { on: l.on } : {}), ...(l.got ? { got: l.got } : {}) });
    });
  }
  for (const q of questionsOf(entries)) {
    const d = questionOf(q);
    const part = (q.parent && parts.get(q.parent)) || null;
    out.push({ key: `${q.id}:asked`, at: q.createdAt, by: d.asked ?? "Your agent", kind: "asked", text: d.text, part, question: q });
    if (d.answer && d.answeredAt) out.push({ key: `${q.id}:answered`, at: d.answeredAt, by: null, kind: "answered", text: d.answer, part, question: q });
  }
  return out.sort((a, b) => a.at - b.at || (a.key < b.key ? -1 : 1));
}

/** The user's notes on a plan from a time on, each with the page that holds it, as fromUserSince hands them to an agent. */
export function userNotesSince(entries: Entry[], since: number, seen: string[] = []): { page: ID; at: number; text: string }[] {
  const pages = entries.filter((e) => e.t === "page");
  const overview = overviewOf(pages);
  const out: { page: ID; at: number; text: string }[] = [];
  for (const page of [...(overview ? [overview] : []), ...partsOf(pages).map((r) => r.page)])
    for (const l of logOf(page)) {
      if (l.by !== null || l.kind !== "note" || l.at < since) continue;
      // the key planEvents gives it, so a note known at that very time is not handed twice
      if (l.at === since && seen.includes(`${page.id}:${l.at}:${l.kind}::${l.text.slice(0, 80)}`)) continue;
      out.push({ page: page.id, at: l.at, text: l.text });
    }
  return out;
}

/** What the user did on the plan from a time on, as lines for an agent: answers, notes and status changes. seen: what was already known at that very time. */
export function fromUserSince(entries: Entry[], since: number, seen: string[] = []): string[] {
  const out: string[] = [];
  for (const e of planEvents(entries)) {
    if (e.by !== null || e.at < since || (e.at === since && seen.includes(e.key))) continue;
    const where = e.part ? `on "${pageTitle(e.part)}"` : "on the whole plan";
    if (e.kind === "answered") out.push(`- Answered "${questionOf(e.question!).text}"${questionOf(e.question!).via ? ` in ${questionOf(e.question!).via}` : ""}: ${e.text}`);
    else if (e.kind === "status" && e.status) out.push(`- Marked "${e.part ? pageTitle(e.part) : "the plan"}" ${STATUS_NAME[e.status].toLowerCase()}${e.text ? `: ${e.text}` : ""}`);
    else out.push(`- Note ${where}: ${e.text}`);
  }
  return out;
}

/** The plan in one line, from what is on it now, so an agent quotes real counts. */
export function planSummary(entries: Entry[]): string {
  const pages = entries.filter((e) => e.t === "page");
  const p = progressOf(pages);
  const n = partsOf(pages).length;
  const counts = PLAN_STATUSES.filter((s) => p.byStatus[s]).map((s) => `${p.byStatus[s]} ${STATUS_NAME[s].toLowerCase()}`);
  const open = needsYou(entries).questions.length;
  // a blocked step stays in front of the user until an agent moves it, so every reply names them (Plan Board Fixes, 22 Oct)
  const blocked = partsOf(pages)
    .map((r) => r.page)
    .filter((x) => statusOf(x) === "blocked")
    .map((x) => {
      const b = blockerOf(x);
      return `"${pageTitle(x)}"${b?.on ? ` (${b.plain ? `waiting on ${b.on}` : b.on.replace(/[.!?]$/, "")})` : ""}`;
    });
  const waiting = partsOf(pages)
    .map((r) => r.page)
    .filter((x) => statusOf(x) === "waiting")
    .map((x) => {
      const b = waitOf(x);
      return `"${pageTitle(x)}"${b?.on ? ` (${b.plain ? `on ${b.on}` : b.on.replace(/[.!?]$/, "")})` : ""}`;
    });
  return `The plan has ${n} step${n === 1 ? "" : "s"}${counts.length ? ` (${counts.join(", ")})` : ""} and ${p.steps.total} task${p.steps.total === 1 ? "" : "s"} (${p.steps.done} done${p.steps.skipped ? `, ${p.steps.skipped} skipped` : ""}); ${open ? `${open} question${open === 1 ? "" : "s"} wait${open === 1 ? "s" : ""} on the user` : "no questions wait on the user"}.${blocked.length ? ` Blocked, in front of the user until moved on: ${blocked.join(", ")}.` : ""}${waiting.length ? ` Waiting on others until moved on: ${waiting.join(", ")}.` : ""}`;
}

const pageTitle = (e: Pick<Entry, "data">) => (typeof e.data.title === "string" && e.data.title.trim() ? e.data.title.trim() : "Untitled");

// ---------------------------------------------------------------------------
// The plan as agents read it, and the words that hand it to a coding agent

/** How many plans' last looks an account keeps. */
export const MAX_PLAN_SEEN = 200;

/**
 * When the user last looked at each plan, merged from a device into the account's: the later of the two for each, so
 * two devices never take each other's look back; the newest MAX_PLAN_SEEN kept (Plan Board Fixes 12, 22 Oct).
 */
export function mergePlanSeen(was: unknown, sent: unknown, now = Date.now()): Record<string, number> {
  const out: Record<string, number> = {};
  for (const src of [was, sent]) {
    if (!src || typeof src !== "object" || Array.isArray(src)) continue;
    for (const [k, v] of Object.entries(src as Record<string, unknown>)) {
      if (!/^[A-Za-z0-9_-]{4,40}$/.test(k) || typeof v !== "number" || !Number.isFinite(v) || v > now + 60_000) continue;
      if (!(k in out) || v > out[k]) out[k] = v;
    }
  }
  return Object.fromEntries(Object.entries(out).sort((a, b) => b[1] - a[1]).slice(0, MAX_PLAN_SEEN));
}

/** The plan's progress: its parts by status, and its steps. Dropped parts, and those set aside for later, count for nothing. */
/** steps: the plan's tasks, done (ticked), skipped (left open on a built step) and all of them, never one for the other. */
export function progressOf(pages: Entry[]): { parts: number; built: number; byStatus: Record<PlanStatus, number>; steps: { done: number; skipped: number; total: number } } {
  const byStatus = Object.fromEntries(PLAN_STATUSES.map((s) => [s, 0])) as Record<PlanStatus, number>;
  const steps = { done: 0, skipped: 0, total: 0 };
  for (const { page } of partsOf(pages)) {
    const s = statusOf(page);
    byStatus[s]++;
    if (isSetAside(s)) continue;
    const c = settledCount(page);
    steps.done += c.done - c.skipped;
    steps.skipped += c.skipped;
    steps.total += c.total;
  }
  const parts = PLAN_STATUSES.reduce((n, s) => n + (isSetAside(s) ? 0 : byStatus[s]), 0);
  return { parts, built: byStatus.built, byStatus, steps };
}

const ago = (t: number, now: number) => {
  const m = Math.round((now - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

function questionLines(qs: Entry[], now: number): string[] {
  return qs.map((e) => {
    const q = questionOf(e);
    const opts = q.options.length ? ` Options: ${q.options.map((o, i) => `"${o.label}"${i === q.recommended ? " (recommended)" : ""}${o.detail ? `: ${o.detail}` : ""}`).join("; ")}.` : "";
    // where it was answered, and a later answer from the other place, which is a note: the first answer stands (Features 3)
    const where = q.via ? `answered in ${q.via}, ` : "";
    const also = q.answer && q.also ? ` Also answered ${q.also.via ? `in ${q.also.via}` : "on the board"} later, kept as a note (the first answer stands): ${q.also.text}` : "";
    return `- ${q.text} (question id ${e.id}${q.asked ? `, asked by ${q.asked}` : ""}${q.kind === "check" ? ", a check" : ""}).${opts} ${q.answer ? `**Answer: ${q.answer}** (${where}${q.answeredAt ? ago(q.answeredAt, now) : ""})${also}` : "_Not answered yet._"}`;
  });
}

/**
 * The whole plan as Markdown, for read_plan: its progress, the questions waiting, the Overview, then every
 * part with its status, its steps' count, its latest news and its questions, then its page. md turns a
 * page's body into Markdown.
 */
export function planMarkdown(board: { id: ID; name: string }, entries: Entry[], md: (body: unknown) => string, now = Date.now()): string {
  const pages = entries.filter((e) => e.t === "page");
  const qs = questionsOf(entries);
  const overview = overviewOf(pages);
  const parts = partsOf(pages);
  const p = progressOf(pages);
  const open = qs.filter((e) => !questionOf(e).answer);
  const out: string[] = [];
  out.push(`# Plan: ${board.name}`);
  const counts = PLAN_STATUSES.filter((s) => p.byStatus[s]).map((s) => `${p.byStatus[s]} ${STATUS_NAME[s].toLowerCase()}`);
  out.push(`Board id ${board.id}. ${parts.length ? `${parts.length} step${parts.length === 1 ? "" : "s"}: ${counts.join(", ")}.` : "No steps yet."} ${p.steps.total ? `Tasks: ${taskTally(p.steps)}.` : ""}`.trim());
  out.push(open.length ? `${open.length} question${open.length === 1 ? "" : "s"} waiting on the user (below, on the step each is about).` : "No questions waiting on the user.");
  // what the user sees in Needs you comes first: a step's newest line can say all is well while it still shows blocked
  // there (Plan Board Fixes, 22 Oct)
  const blocked = parts.filter(({ page }) => statusOf(page) === "blocked");
  if (blocked.length) {
    out.push("", "## Blocked steps, as the user sees them in Needs you");
    for (const { page } of blocked) {
      const b = blockerOf(page);
      out.push(`- "${pageTitle(page)}" (page id ${page.id}) ${blockedWords(b).replace(/[.!?]$/, "")}${b?.since ? ` (blocked ${ago(b.since, now)})` : ""}.`);
    }
    out.push("Each stays in front of the user until it is moved on: when what it waits on has happened, update_plan it to building, or built with what proves it.");
  }
  const waiting = parts.filter(({ page }) => statusOf(page) === "waiting");
  if (waiting.length) {
    out.push("", "## Steps waiting on others (not on the user)");
    for (const { page } of waiting) {
      const b = waitOf(page);
      out.push(`- "${pageTitle(page)}" (page id ${page.id}) ${waitingWords(b).replace(/[.!?]$/, "")}${b?.since ? ` (since ${ago(b.since, now)})` : ""}.`);
    }
    out.push("When what one waits on has come through, update_plan it to building, or built with what proves it; meanwhile carry on with another step.");
  }
  const at = (parent: ID | null) => qs.filter((e) => (e.parent ?? null) === parent);
  const top = at(null).concat(overview ? at(overview.id) : []);
  if (top.length) out.push("", "## Questions about the whole plan", ...questionLines(top, now));
  // the user's own notes: what they want the agent to know, left on the plan rather than said in a chat
  const notes = (page: Entry) =>
    logOf(page)
      .filter((l) => l.by === null && l.kind === "note")
      .slice(-8)
      .map((l) => `- ${l.text} (${ago(l.at, now)})`);
  if (overview && notes(overview).length) out.push("", "## Notes from the user about the whole plan", ...notes(overview));
  if (overview) {
    out.push("", `## Overview (page id ${overview.id})`, md(overview.data.body) || "(No writing yet.)");
  }
  parts.forEach(({ page, depth }, i) => {
    const s = statusOf(page);
    const c = stepCount(page.data.body);
    const log = logOf(page);
    const last = log[log.length - 1];
    const head = depth === 0 ? "##" : "#".repeat(Math.min(6, 2 + depth));
    out.push("", `${head} ${depth === 0 ? `Step ${parts.filter((r, j) => r.depth === 0 && j <= i).length}: ` : ""}${page.data.icon ? `${page.data.icon} ` : ""}${typeof page.data.title === "string" && page.data.title.trim() ? page.data.title.trim() : "Untitled"} (page id ${page.id})`);
    const b = blockerOf(page) ?? waitOf(page);
    const why = b ? `${b.on ? (b.plain ? `, waiting on ${b.on}` : `: ${b.on.replace(/[.!?]$/, "")}`) : ""}${b.since ? ` (${s === "waiting" ? "since" : "blocked"} ${ago(b.since, now)})` : ""}` : "";
    const skipped = s === "built" ? c.total - c.done : 0;
    out.push(`Status: **${STATUS_NAME[s]}**${why}.${c.total ? ` Tasks: ${c.done} of ${c.total} done${skipped ? `; built, so the ${skipped} left open count as skipped` : ""}.` : " No tasks yet."}${last ? ` Latest: ${lineWords(last)} (${last.by ?? "the user"}, ${ago(last.at, now)}).` : ""}`);
    const mine = at(page.id);
    if (mine.length) out.push("Questions:", ...questionLines(mine, now));
    if (notes(page).length) out.push("Notes from the user:", ...notes(page));
    out.push("", md(page.data.body) || "(No writing yet.)");
  });
  return out.join("\n");
}

// ---------------------------------------------------------------------------
// Where the build stands, so a plan handed off again starts there rather than at its first step

/** A step of the plan as Hand off counts it: numbered as the route and read_plan number the steps. */
export interface StepStanding {
  n: number;
  id: ID;
  title: string;
  status: PlanStatus;
  /** a blocked step: what it waits on, as the user reads it */
  waiting?: string;
}

export interface Standing {
  /** the top-level steps, in order */
  steps: StepStanding[];
  /** the first step neither built nor dropped */
  next: StepStanding | null;
  /** where the build carries on: the first step left that isn't blocked, else the first left */
  start: StepStanding | null;
  /** steps inside a step still to build that are built themselves, by their names */
  inside: { n: number; titles: string[] }[];
  /** questions on the plan not answered yet */
  open: number;
}

export function planStanding(entries: Entry[]): Standing {
  const steps: StepStanding[] = [];
  const inside = new Map<number, string[]>();
  let top: StepStanding | null = null;
  for (const { page, depth } of partsOf(entries.filter((e) => e.t === "page"))) {
    if (depth === 0) {
      const b = blockerOf(page);
      const w = waitOf(page);
      top = { n: steps.length + 1, id: page.id, title: pageTitle(page), status: statusOf(page), ...(b?.on ? { waiting: blockedWords(b).replace(/^is blocked(, |: )?/, "") } : w?.on ? { waiting: waitingWords(w).replace(/^is waiting(: | on )?/, "") } : {}) };
      steps.push(top);
    } else if (top && !isDone(top.status) && statusOf(page) === "built") inside.set(top.n, [...(inside.get(top.n) ?? []), pageTitle(page)]);
  }
  const next = steps.find((s) => !isDone(s.status)) ?? null;
  const start = steps.find((s) => !isDone(s.status) && s.status !== "blocked" && s.status !== "waiting") ?? next;
  return { steps, next, start, inside: [...inside].map(([n, titles]) => ({ n, titles })), open: questionsOf(entries, true).length };
}

const isDone = (s: PlanStatus) => s === "built" || isSetAside(s);

/** Whether anything has happened to the plan's steps yet: one built, being built, blocked or dropped. */
export const hasStarted = (st: Standing) => st.steps.some((s) => s.status !== "planned") || st.inside.length > 0;

/** Step numbers as said: 1 to 4 and 6; 1, 2 and 5 to 7. Runs of three or more are a span. */
export function numberWords(ns: number[]): string {
  const runs: string[] = [];
  for (let i = 0; i < ns.length; ) {
    let j = i;
    while (j + 1 < ns.length && ns[j + 1] === ns[j] + 1) j++;
    if (j - i >= 2) runs.push(`${ns[i]} to ${ns[j]}`);
    else for (let k = i; k <= j; k++) runs.push(String(ns[k]));
    i = j + 1;
  }
  return runs.length <= 1 ? runs.join("") : `${runs.slice(0, -1).join(", ")} and ${runs[runs.length - 1]}`;
}

const stepsWord = (ns: number[]) => `${ns.length === 1 ? "Step" : "Steps"} ${numberWords(ns)}`;
const named = (s: StepStanding) => `step ${s.n}, "${s.title}"`;
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const quoted = (ts: string[]) => {
  const q = ts.map((t) => `"${t}"`);
  return q.length <= 1 ? q.join("") : `${q.slice(0, -1).join(", ")} and ${q[q.length - 1]}`;
};

/** Where the build stands, for the agent it is handed to again: a line each, built first, then where to carry on. */
export function standingLines(st: Standing): string[] {
  const of = (s: PlanStatus) => st.steps.filter((x) => x.status === s).map((x) => x.n);
  const built = of("built");
  const dropped = of("dropped");
  const later = of("later");
  const one = built.length === 1;
  const out: string[] = [];
  if (built.length)
    out.push(`- ${stepsWord(built)} ${one ? "is" : "are"} built. Don't build ${one ? "it" : "them"} again, but read ${one ? "it" : "them"} first for context: what ${one ? "it does" : "each one does"}, the files ${one ? "it touches" : "they touch"}, what proves ${one ? "it works" : "they work"}, and any change made to the plan on the way.`);
  if (dropped.length) out.push(`- ${stepsWord(dropped)} ${dropped.length === 1 ? "was" : "were"} dropped, so skip ${dropped.length === 1 ? "it" : "them"}.`);
  if (later.length) out.push(`- ${stepsWord(later)} ${later.length === 1 ? "is" : "are"} set aside for later, so leave ${later.length === 1 ? "it" : "them"} unless I say.`);
  const left = st.steps.filter((s) => !isDone(s.status));
  for (const s of left) {
    const first = s === st.start;
    if (s.status === "building") out.push(`- ${cap(named(s))}, was being built: a session before you started it. Read its tasks and history to see how far it got, and finish it${first ? " first" : ""}.`);
    else if (s.status === "blocked") out.push(`- ${cap(named(s))}, is blocked${s.waiting ? ` (${s.waiting.replace(/[.!?]$/, "")})` : ""}. Read why in its history, and my answers on the plan. If what it waits on has happened, move it on; if it still can't go on, carry on with another step meanwhile.`);
    else if (s.status === "waiting") out.push(`- ${cap(named(s))}, is waiting on others${s.waiting ? ` (${s.waiting.replace(/[.!?]$/, "")})` : ""}. If that has come through, move it on; if not, carry on with another step meanwhile.`);
    else if (first) out.push(`- ${cap(named(s))}, is next.`);
    const ins = st.inside.find((i) => i.n === s.n);
    if (ins) out.push(`- Inside step ${s.n}, ${quoted(ins.titles)} ${ins.titles.length === 1 ? "is" : "are"} built already.`);
  }
  const after = left.filter((s) => s !== st.start && s.status === "planned").map((s) => s.n);
  if (after.length) out.push(`- After that, ${after.length === 1 ? "step" : "steps"} ${numberWords(after)} ${after.length === 1 ? "is" : "are"} still to build.`);
  if (st.open) out.push(`- ${st.open} question${st.open === 1 ? "" : "s"} on the plan still wait${st.open === 1 ? "s" : ""} on me.`);
  return out;
}

/** Where it stands in a line, for the person handing it off: null before anything is built. */
export function standingSummary(st: Standing): string | null {
  if (!hasStarted(st)) return null;
  const counted = st.steps.filter((s) => !isSetAside(s.status)).length;
  const built = st.steps.filter((s) => s.status === "built").length;
  // as a person reads it: built is done (Plan Board Fixes 10)
  if (!st.start) return built ? `Every step is done. Your agent reads the plan for context, then asks you what's next.` : st.steps.some((s) => s.status === "later") ? `Every step left is set aside for later. Your agent reads the plan, then asks you what's next.` : `Every step was dropped. Your agent reads the plan, then asks you what's next.`;
  const done = built ? `${built} of ${counted} steps done, so it` : "It";
  return st.start.status === "building" ? `${done} finishes step ${st.start.n}, “${st.start.title}”, first.` : `${done} carries on from step ${st.start.n}, “${st.start.title}”.`;
}

/**
 * A question asked on the plan shows in the coding session too, and is answered in either place (Features 3, David,
 * 22 Oct): the session's own question picker would keep the board's answer out, so it is a plain message.
 */
export const IN_SESSION =
  "Show every question you put on the plan here in this session too, as a plain message with its options numbered (ask_plan's reply has it ready), and say I can answer here or on the board. Don't use your own question tool for it: that would keep my answer on the board from reaching you. When I answer here (a number or my own words), record it with answer_plan before you go on. When wait_for_answer hands you an answer I gave on the board, say \"Answered on the board: ...\" here, then carry on.";

/** How a coding agent keeps the plan current as it builds: the same for a first hand off and a later one. */
const BUILD_RULES = [
  "Keep the plan up to date with update_plan as you go. The plan has a chat I read to see where the build is, so give each update_plan a message: one or two short plain sentences, the way a colleague would message me. For example: \"I've finished the voice model. It downloads once and works offline. Now I'm on Hold to talk.\" Say what I would notice, with no lists, technical words or figures (file names, commits, branches, tokens, test counts, ratios): those go in note, which stays in the step's history.",
  "- status building when you start a step;",
  "- done with the words of each task as you finish it;",
  "- status built, with a note saying what proves it (the test that passed, the commit, where to see it), when a step is done;",
  "- pictures on update_plan for anything I'd want to see: send any mockup you make (an HTML mockup as a screenshot of it), and a screenshot of the result whenever a step changes how something looks, each with a short caption, and before and after as a pair when it changes something already there. I see them in the plan's Previews;",
  "- change true whenever you do something differently from the plan, with was (what the plan said) and now (what you're doing instead), a few plain words each, and why, one plain sentence: I see them side by side, so keep the technical detail for note;",
  "- when you need me to decide or to try something, ask_plan (with kind check when it's something for me to try, sign in to or paste, so it stays apart from what I decided; status blocked if you can't go on) and say so in your message, then carry on with another step meanwhile; when nothing else can go on without my answer, call wait_for_answer, and again while it says I haven't answered: it hands you my answer the moment I give it on the board, so you carry on without my coming back here;",
  "- when a step can't go on, status blocked if it needs me (a decision, or something only I can do: ask_plan asks me), or status waiting if it waits on someone outside the build (Google's review, a customer's reply), each with waiting_on, a few plain words I read at a glance (\"Google's store review\"); it stays in front of me until you move it, so when what it waits on has happened, move it on in that same update (building, or built with what proves it);",
  "- status later, with a note saying why, for a step set aside on purpose; status dropped, with a note saying why, for one no longer needed.",
  "",
  "I answer questions and leave notes on the plan board. update_plan's reply tells you what I answered or said since you last looked, and read_plan shows it all: act on it before you go on. When I tell you something here that changes the plan (an approval came through, a page went live), update the plan in that same reply.",
  IN_SESSION,
  "Keep each step's page true to what you build: when a decision or a change makes lines of it wrong (how it works, its tasks), fix those lines with write_page in the same go, and pass change true on update_plan with was, now and why, so I see what changed. Don't rewrite the rest of a page; if the plan itself is wrong, tell me.",
  "If the context-board tools aren't available, stop and tell me to connect the Context Board MCP (Connect, in the board).",
];

/**
 * What a coding agent is given to build the plan with: where it is, and how to keep it up to date. Handed off
 * again once some of it is built, it says where the build stands and where to carry on, so a new session starts
 * there and reads what is built for context rather than building it again.
 */
export function handoffPrompt(board: { id: ID; name: string }, entries: Entry[] = []): string {
  const where = `my Context Board plan board "${board.name}" (board id ${board.id})`;
  const read = "The plan is on the Context Board MCP server (context-board). Before you start, call read_plan with that board and read the whole plan, with my answers to its questions.";
  const st = planStanding(entries);
  if (!hasStarted(st)) return [`Build the plan on ${where}, here in this project.`, "", `${read} Build the steps in the order the plan gives, unless it says otherwise.`, "", ...BUILD_RULES].join("\n");
  const built = st.steps.filter((s) => s.status === "built").map((s) => s.n);
  if (!st.start) {
    const dropped = st.steps.filter((s) => s.status === "dropped").map((s) => s.n);
    const later = st.steps.filter((s) => s.status === "later").map((s) => s.n);
    return [
      `${built.length ? "Every step of the plan" : "Nothing is left to build on the plan"} on ${where}${built.length ? (later.length ? " is built or set aside for later" : " is built") : later.length ? ": every step left is set aside for later" : ": every step was dropped"}. Pick it up here in this project.`,
      "",
      "Where it stands:",
      ...(built.length ? [`- ${stepsWord(built)} ${built.length === 1 ? "is" : "are"} built.`] : []),
      ...(dropped.length ? [`- ${stepsWord(dropped)} ${dropped.length === 1 ? "was" : "were"} dropped.`] : []),
      ...(later.length ? [`- ${stepsWord(later)} ${later.length === 1 ? "is" : "are"} set aside for later.`] : []),
      ...(st.open ? [`- ${st.open} question${st.open === 1 ? "" : "s"} on the plan still wait${st.open === 1 ? "s" : ""} on me.`] : []),
      "",
      `${read}${built.length ? " Read the built steps for context: what each one does, the files it touches and what proves it works." : ""} Then ask me with ask_plan what's next, unless I've said so here. If you build more, keep the plan up to date as below.`,
      "",
      ...BUILD_RULES,
    ].join("\n");
  }
  return [
    `Carry on building the plan on ${where}, here in this project. ${built.length ? "Some of it is built already" : "Work on it has started"}, so start from where it stands.`,
    "",
    "Where it stands:",
    ...standingLines(st),
    "",
    `${read} Then build what's left in the plan's order, starting with step ${st.start.n}.`,
    "",
    ...BUILD_RULES,
  ].join("\n");
}

// ---------------------------------------------------------------------------
// The words that start a plan: what the user said it is for, to the board's agent or to their coding agent

/** What the plan is for, from the plan's first form: talked through, typed, or both. */
export interface PlanBrief {
  spoken?: string;
  what?: string;
  who?: string;
  done?: string;
  rules?: string;
}

/** Whether a plan board still has the name a new one starts with (src/boards/start.ts), before its agent names it for what
 * is planned: Plan, Plan 2 and on, or New plan from before Fixes 12. */
const isNewPlan = (name: string) => /^(?:new )?plan(?: \d+)?$/i.test(name.trim());

function briefLines(b: PlanBrief): string[] {
  const t = (s?: string) => (s ?? "").replace(/\s+/g, " ").trim();
  return [
    t(b.spoken) ? `I talked it through out loud, so a word here or there may be misheard:\n"${t(b.spoken)}"` : "",
    t(b.what) ? `What I'm building: ${t(b.what)}` : "",
    t(b.who) ? `Who it's for: ${t(b.who)}` : "",
    t(b.done) ? `What done looks like: ${t(b.done)}` : "",
    t(b.rules) ? `It must, or must not: ${t(b.rules)}` : "",
  ].filter(Boolean);
}

/** Whether the form holds anything to plan from. */
export const hasBrief = (b: PlanBrief) => briefLines(b).length > 0;

const ASK_FIRST =
  "Before you write any steps, ask me, with ask_plan, what would change them: what's in and what's out, which way to go where there's a real choice, what matters most, and what to build first. Give each question options to click, with your pick marked, and don't ask what my words or my project already answer. If nothing is left to decide, go straight on to the plan.";

const WRITE_PLAN =
  "Then write the plan with write_page: fill in the Overview (the goal, who it's for, what done looks like, and a ```mermaid diagram of how the steps fit together), and make one top-level page per step with what it does, how it works, its tasks as - [ ] to-dos, and the files it touches. Keep each step small enough to build and check on its own, in the order to build them, and write my answers into the steps they decide. Put anything still undecided on the plan with ask_plan.";

const nameIt = (board: { name: string }) => (isNewPlan(board.name) ? ` The board is still called "${board.name.trim()}": name it for what we're planning, with update_board.` : "");

/** For the board's own agent, in its chat: it asks on the plan, the board tells it once the user has answered, and it writes the plan. */
export function planHerePrompt(board: { id: ID; name: string }, brief: PlanBrief): string {
  return [
    `Plan this with me on the plan board "${board.name}" (board id ${board.id}). Plan only: don't build anything or write code. My coding agent builds it afterwards, from this plan.`,
    "",
    ...briefLines(brief),
    "",
    `Read my project's files if you can reach them. ${ASK_FIRST} Then stop: I answer on the plan, and the board tells you when I have. ${WRITE_PLAN}${nameIt(board)} Keep your reply here to a few lines: the plan is on the board.`,
  ].join("\n");
}

/** For the user's coding agent, pasted where it works: it reads the project, asks on the plan and waits there for the answers, writes the plan, then stops until it is handed the plan to build. */
export function planTherePrompt(board: { id: ID; name: string }, brief: PlanBrief): string {
  return [
    `Plan this with me on my Context Board plan board "${board.name}" (board id ${board.id}), here in this project. Plan only for now: don't build anything or change any code until I hand you the plan to build.`,
    "",
    ...briefLines(brief),
    "",
    `The plan board is on the Context Board MCP server (context-board). First read this project, so the plan fits the code that's here. ${ASK_FIRST} Then call wait_for_answer, and again until I've answered them all: it hands you each answer the moment I give it on the board. ${IN_SESSION} ${WRITE_PLAN}${nameIt(board)}`,
    "",
    "When the plan is written, tell me so in a line or two and stop. I read it on the board, and when it's right I hand it to you to build, with Hand off.",
    "If the context-board tools aren't available, stop and tell me to connect the Context Board MCP (Connect, in the board).",
  ].join("\n");
}

/** Whether an Overview still holds only the starter's headings, nothing written under them. */
export function isStarterOverview(body: unknown): boolean {
  const want = starterOverview().content;
  const text = (n: DocNode) => (n.content ?? []).map((c) => c.text ?? "").join("");
  const got = (body as DocBody | null | undefined)?.content ?? [];
  return got.length === want.length && got.every((n, i) => n.type === want[i].type && text(n) === text(want[i]));
}

/** What a new plan's Overview says before anything is planned: the headings the board's agent fills in. */
export function starterOverview(): DocBody {
  const h = (text: string): DocNode => ({ type: "heading", attrs: { level: 2 }, content: [{ type: "text", text }] });
  return { type: "doc", content: [h("The goal"), { type: "paragraph" }, h("Who it is for"), { type: "paragraph" }, h("What done looks like"), { type: "paragraph" }, h("How the steps fit together"), { type: "paragraph" }] };
}
